import { Component, Inject, OnInit } from '@angular/core';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { clone, defaults } from 'lodash-es';
import { ButtonSize, ButtonStyle } from '../../../aero/aero-button/aero-button.component';
// import { AeroDialogService } from '../../../aero/aero-dialog/aero-dialog.service';

import { TranslationService } from '../../../common/translation.service';
import { HttpService } from '../../page-view/http.service';
import { TagsDialogComponent } from '../tags-dialog/tags-dialog.component';
import { DialogConfig } from '../../../aero/aero-dialog/types';
import { DialogComponent } from '../../../aero/aero-dialog/dialog/dialog.component';

interface Tag {
  id?: string;
  name: string;
  description: string;
}

@Component({
  selector: 'bulb-tags-student-dialog',
  templateUrl: './tags-student-dialog.component.html',
  styleUrls: ['./tags-student-dialog.component.scss'],
  standalone: false,
})
export class TagsStudentDialogComponent implements OnInit {

  smallButton: ButtonSize.SLIM;

  selectedTags = [];

  readonly MAX_SELECTION = 3;

  unit: any;

  pageId: string;

  organizationId: string;

  organizationTags = [];

  myTags = [];

  recentTags = [];

  constructor(
    private config: DynamicDialogConfig,
    // private aeroDialogService: AeroDialogService,
    private dialogService : DialogService,
    private $translate: TranslationService,
    private dynamicDialogRef: DynamicDialogRef,
    private httpService: HttpService,
    @Inject('BulbErrorService') private bulbErrorService,
  ) {
  }

  ngOnInit(): void {
    this.organizationId = this.config.data.organizationId;
    this.unit = this.config.data.unit;
    this.pageId = this.unit.id;
    this.selectedTags = this.unit.tags || [];
    this.loadTags();
  }

  loadTags() {
    this.httpService.loadTagsForUser()
      .subscribe({
        next: (tags: any) => {
          this.organizationTags = tags.organizationTags || [];
          this.recentTags = tags.recentTags || [];
          this.myTags = tags.myTags || [];
        },
        error: (err) => {
          this.bulbErrorService.showError(null, err);
        },
      });
  }

  isCheckboxDisabled(tag: Tag): boolean {
    return (
      this.selectedTags.length >= this.MAX_SELECTION
      && !this.selectedTags.some((t) => t.id === tag.id)
    );
  }

  removeSelectedTag(tag: Tag): void {
    this.httpService.removeSelectedTag(this.pageId, tag.id).subscribe(
      {
        next: () => {
          const index = this.selectedTags.findIndex((t) => t.id === tag.id);

          if (index !== -1) {
            this.selectedTags.splice(index, 1);

            // 🔑 force PrimeNG table to sync selection
            this.selectedTags = [...this.selectedTags];
          }
        },
        error: (err) => {
          this.bulbErrorService.showError(null, err);
        },
      },
    );
  }

  /**
   * Show the dialog for copy template.
   */
  showTagsDialog(organizationId: string, tag?: any, isOrgTag: boolean = true) : DynamicDialogRef {
    const copyTemplateOptions = {
      width: '25rem',
      height: 'auto',
      closeOnEscape: true,
      dismissableMask: true,
      showHeader: false,
      styleClass: 'aero-dialog',
      data: { organizationId, tag, isOrgTag },
    };

    return this.dialogService.open(TagsDialogComponent, copyTemplateOptions);
  }

  createTag() {
    if (this.myTags.length < 20) {
      const dialog = this.showTagsDialog(null, null, false);

      const subscription = dialog.onClose.subscribe((createdTag) => {
        if (createdTag) {
          this.myTags.push(angular.copy(createdTag));
        }
        // this.closeDialog();
        // this.aeroDialogService.closeDialog();
        subscription.unsubscribe();
      });
    }
  }

  editTag(tag) {
    const dialog = this.showTagsDialog(null, tag, false);

    const subscription = dialog.onClose.subscribe((updatedTag) => {
      if (updatedTag) {
        const index = this.myTags.findIndex(
          (myTag) => myTag.id === updatedTag.id,
        );

        if (index !== -1) {
          this.myTags[index] = angular.copy(updatedTag);

          // ensure UI refresh (important for PrimeNG)
          this.myTags = [...this.myTags];
        }
      }

      // this.closeDialog();
      // this.aeroDialogService.closeDialog();
      subscription.unsubscribe();
    });
  }

  /**
   * The default configuration for standard aero dialogs.
   */
  defaultDialogConfigs : DialogConfig = {
    // Settings specific to the aero standard dialog
    primaryStyle: ButtonStyle.PRIMARY,
    primaryDisabled: false,
    closeOnPrimary: true,
    secondaryStyle: ButtonStyle.SECONDARY,
    secondaryDisabled: false,
    closeOnSecondary: true,
    showSecondary: true,
    showFooter: true,
    showInputField: false,

    // Settings for primeng dynamic dialog
    width: '352px',
    closable: true,
    closeOnEscape: true,
    dismissableMask: true,

    // This hides the standard primeng header so that we can use our own header
    showHeader: false,
  };

  /**
   * Create and show a standard aero dialog.  See DialogConfig for example usage and config properties.
   *
   * @param config Configuration for the standard dialog.
   */
  showDialog(config : DialogConfig) {

    // Make a copy of the config, so we can manipulate it.
    const configClone = clone(config);

    // Update the config with our standard defaults for any values not specified.
    defaults(configClone, this.defaultDialogConfigs);

    return this.dialogService.open(DialogComponent, configClone);
  }

  /**
   * Show Confirm dialog.
   */
  showConfirmDialog(tag) {
    this.showDialog({
      icon: 'far fa-trash-alt',
      iconClass: 'aero-alert',
      title: this.$translate.translateInstant('organization_turn_off_integration_modal_title'),
      detail: 'Do you really want to delete this tag?',
      primaryLabel: this.$translate.translateInstant('button_delete'),
      primaryStyle: ButtonStyle.NEGATIVE_ACTION,
      onPrimary: () => {
        this.deleteTag(tag);
      },
    });
  }

  deleteTag(tag) {
    const index = this.myTags.findIndex(
      (myTag) => myTag.id === tag.id,
    );

    this.httpService.deleteTag(tag.id)
      .subscribe({
        next: () => {
          this.myTags.splice(index, 1);
          // ensure UI refresh (important for PrimeNG)
          this.myTags = [...this.myTags];
        },
        error: (error) => {
          this.bulbErrorService.showError(null, error);
        },
      });
  }

  onTagChecked(event: any) {
    const tag = event.data;
    this.httpService.selectedTag(this.pageId, tag.id).subscribe({
      next: () => {
        // Do nothing
      },
      error: (err) => {
        // 🔁 rollback selection
        this.selectedTags = this.selectedTags.filter(
          (t: any) => t.id !== tag.id,
        );
        this.bulbErrorService.showError(this.$translate.translateInstant(err.error.developerMessage), err);
      },
    });
  }

  onTagUnchecked(event: any) {
    const tag = event.data;
    this.httpService.removeSelectedTag(this.pageId, tag.id).subscribe(
      {
        next: () => {
          // Do nothing
        },
        error: (err) => {
          this.bulbErrorService.showError(null, err);
        },
      },
    );
  }

  closeDialog(): void {
    this.dynamicDialogRef.close();
  }
}
